import { useState, useEffect } from 'react'
import { styled } from '@mui/material/styles'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import IconButton from '@mui/material/IconButton'
import { DatePicker } from '@mui/x-date-pickers-pro'
import DeleteIcon from '@mui/icons-material/Delete'
import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Controller, useFormContext } from 'react-hook-form'
import { isSameDay } from 'date-fns/isSameDay'
import { toast } from 'react-toastify'

import {
  useOptionsTypeOptions,
  getOption,
  formatHumanDateTime,
  formatHumanDate,
  formatHumanTime,
} from '../../../../../utils'
import { useAppSelector } from '../../../../../store'
import { getEventDetails } from '../../../../../store.slice'
import { useDeleteOptionMutation } from '../../../../../services/events'
import {
  SessionOptionsSchema,
  SubEventOptionsSchema,
  CheckboxOptionsSchema,
  TextOptionsSchema,
  NumberOptionsSchema,
  OptionsBaseSchema,
} from '../../../../events/Events.schema'
import type { ModifyEventSchema } from '../../../../events/Events.schema'
import { OptionType, type OptionsBase } from '../../../../../types'
import { StatusTag } from '../../../../root/components/StatusTag'

const Span = styled('span')<{ lineHeight?: number; bold?: boolean }>(
  ({ theme, lineHeight, bold }) => ({
    color: theme.palette.text.secondary,
    lineHeight: lineHeight ?? undefined,
    fontWeight: bold ? 'bold' : 'normal',
  })
)

interface OptionFormProps {
  optionKey: number
  onRemove: () => void
  forceDetails?: boolean
}

const sessionOptionsSchemaKeys = SessionOptionsSchema.keyof().options
const subEventOptionsSchemaKeys = SubEventOptionsSchema.keyof().options
const checkboxOptionsSchemaKeys = CheckboxOptionsSchema.keyof().options
const textOptionsSchemaKeys = TextOptionsSchema.keyof().options
const numberOptionsSchemaKeys = NumberOptionsSchema.keyof().options
const defaultOptionsSchemaKeys = OptionsBaseSchema.keyof().options

export default function OptionForm({ optionKey, onRemove, forceDetails = false }: OptionFormProps) {
  const { t } = useTranslation('events')
  const optionsTypeOptions = useOptionsTypeOptions(t)
  const { eventId } = useParams()
  const [showDetails, setShowDetails] = useState(forceDetails)
  const [acceptedKeys, setAcceptedKeys] =
    useState<(keyof OptionsBase | '_new')[]>(defaultOptionsSchemaKeys)

  const [deleteOption] = useDeleteOptionMutation()

  const eventDetails = useAppSelector(state => getEventDetails(state)(eventId))
  const { control, watch, getValues } = useFormContext<ModifyEventSchema>()

  const name = watch(`options.${optionKey}.name`)
  const type = watch(`options.${optionKey}.type`)
  const registrationInformation = watch(`options.${optionKey}.registration_information`)
  const maxParticipants = watch(`options.${optionKey}.max_participants`)
  const deadline = watch(`options.${optionKey}.deadline`)
  const maxDateTime = eventDetails?.event?.end ? new Date(eventDetails.event.end) : undefined
  const sessions = eventDetails?.sessions ?? []
  const subEvents = eventDetails?.subEvents ?? []

  const typeOption = getOption(optionsTypeOptions, null, type)

  useEffect(() => {
    switch (type) {
      case OptionType.SessionOption:
        setAcceptedKeys(sessionOptionsSchemaKeys)
        break
      case OptionType.SubEventOption:
        setAcceptedKeys(subEventOptionsSchemaKeys)
        break
      case OptionType.CheckBoxOption:
        setAcceptedKeys(checkboxOptionsSchemaKeys)
        break
      case OptionType.TextOption:
        setAcceptedKeys(textOptionsSchemaKeys)
        break
      case OptionType.NumberOption:
        setAcceptedKeys(numberOptionsSchemaKeys)
        break
      default:
        setAcceptedKeys(defaultOptionsSchemaKeys)
        break
    }
  }, [type])

  return (
    <Stack width="100%">
      <h5>
        {t('Condition de souscription')} {optionKey + 1} : {name}
      </h5>

      <Stack
        spacing={1}
        padding={2}
        sx={theme => ({
          backgroundColor: theme.colors.greyBackground,
          borderRadius: '0 0 1rem 1rem',
          borderTop: `0.15rem solid ${theme.colors.secondary}`,
        })}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Span>{t('Détails de la condition de souscription')}</Span>
          <Stack gap="0.5rem" direction="row">
            <IconButton
              onClick={event => {
                event.preventDefault()
                if (
                  window.confirm(
                    t(
                      'Êtes-vous sûr de vouloir supprimer cette option ? La suppression sera immédiate en base de données mais les interventions ou séances rattachées ne seront pas supprimées'
                    ) + '.'
                  )
                ) {
                  const id = getValues(`options.${optionKey}.id`)
                  const isNew = getValues(`options.${optionKey}._new`)
                  if (!isNew) {
                    deleteOption({ eventId: Number(eventId), id: id })
                      .then(data => {
                        if ('data' in data) {
                          toast.success(t('Option supprimée'))
                        }
                      })
                      .catch(error => {
                        console.error(error)
                        toast.error(t('Une erreur est survenue'))
                      })
                  }

                  onRemove()
                }
              }}
            >
              <DeleteIcon fontSize="inherit" />
            </IconButton>
            {!forceDetails && (
              <IconButton
                onClick={event => {
                  event.preventDefault()
                  setShowDetails(prev => !prev)
                }}
              >
                <ArrowDropDownCircleIcon
                  fontSize="inherit"
                  sx={{
                    transform: showDetails ? 'rotate(180deg)' : undefined,
                    transition: 'transform 0.3s',
                  }}
                />
              </IconButton>
            )}
          </Stack>
        </Stack>
        {showDetails ? (
          <>
            <Controller
              control={control}
              name={`options.${optionKey}.name`}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label={t('Nom')}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  fullWidth
                  required
                  sx={{
                    display: !acceptedKeys.includes('name') ? 'none' : undefined,
                  }}
                />
              )}
            />
            <Controller
              control={control}
              name={`options.${optionKey}.type`}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <Autocomplete
                  onChange={(_, value) => field.onChange(value?.value ?? '')}
                  onBlur={field.onBlur}
                  defaultValue={getOption(optionsTypeOptions, null, field.value) ?? undefined}
                  options={optionsTypeOptions}
                  id="type-autocomplete"
                  disablePortal
                  fullWidth
                  disableClearable
                  renderInput={params => (
                    <TextField
                      {...params}
                      name={field.name}
                      disabled={field.disabled}
                      label={t('Type')}
                      required
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      sx={{
                        display: !acceptedKeys.includes('type') ? 'none' : undefined,
                      }}
                    />
                  )}
                />
              )}
            />
            <Controller
              control={control}
              name={`options.${optionKey}.sub_event`}
              render={({ field, fieldState }) => (
                <Autocomplete
                  onChange={(_, value) => field.onChange(value?.id ?? '')}
                  defaultValue={subEvents.find(el => el.id === field.value) ?? undefined}
                  onBlur={field.onBlur}
                  options={subEvents}
                  id="subeventid-autocomplete"
                  disablePortal
                  fullWidth
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  filterOptions={x => x}
                  getOptionLabel={option => {
                    const sameDay =
                      option.start && option.end ? isSameDay(option.start, option.end) : false
                    return `${formatHumanDateTime(option.start, true)}-${sameDay ? formatHumanTime(option.end) : formatHumanDateTime(option.end, true)} : ${option.name}`
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      name={field.name}
                      disabled={field.disabled}
                      label={t('Intervention rattachée')}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      sx={{
                        display: !acceptedKeys.includes('sub_event') ? 'none' : undefined,
                      }}
                    />
                  )}
                />
              )}
            />
            <Controller
              control={control}
              name={`options.${optionKey}.session`}
              render={({ field, fieldState }) => (
                <Autocomplete
                  onChange={(_, value) => field.onChange(value?.id ?? '')}
                  defaultValue={sessions.find(el => el.id === field.value) ?? undefined}
                  onBlur={field.onBlur}
                  options={sessions}
                  id="sessionid-autocomplete"
                  disablePortal
                  fullWidth
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  filterOptions={x => x}
                  getOptionLabel={option => {
                    const sameDay =
                      option.start && option.end ? isSameDay(option.start, option.end) : false
                    return `${formatHumanDateTime(option.start, true)}-${sameDay ? formatHumanTime(option.end) : formatHumanDateTime(option.end, true)} : ${option.name}`
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      name={field.name}
                      disabled={field.disabled}
                      label={t('Séance rattachée')}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      sx={{
                        display: !acceptedKeys.includes('session') ? 'none' : undefined,
                      }}
                    />
                  )}
                />
              )}
            />
            <Controller
              control={control}
              name={`options.${optionKey}.registration_information`}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label={t('Informations d’inscription')}
                  fullWidth
                  multiline
                  rows={4}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  sx={{
                    display: !acceptedKeys.includes('registration_information')
                      ? 'none'
                      : undefined,
                  }}
                />
              )}
            />
            <Controller
              control={control}
              name={`options.${optionKey}.deadline`}
              render={({ field, fieldState }) => (
                <DatePicker
                  label={t('Date limite')}
                  name={field.name}
                  maxDate={maxDateTime}
                  defaultValue={field.value ? new Date(field.value) : null}
                  onChange={date => {
                    field.onChange(date?.toISOString() ?? '')
                  }}
                  sx={{
                    flex: '1 0 auto',
                    display: !acceptedKeys.includes('deadline') ? 'none' : undefined,
                  }}
                  slotProps={{
                    textField: {
                      error: !!fieldState.error,
                      helperText: fieldState.error?.message,
                    },
                  }}
                />
              )}
            />
            <Controller
              control={control}
              name={`options.${optionKey}.max_participants`}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label={t('Nombre max participants')}
                  fullWidth
                  multiline
                  type="number"
                  onChange={e => {
                    const { value } = e.target
                    if (value && typeof value === 'string') {
                      const parsed = Number(value)
                      field.onChange(Number.isNaN(parsed) ? null : parsed)
                    } else {
                      field.onChange(null)
                    }
                  }}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  sx={{
                    display: !acceptedKeys.includes('max_participants') ? 'none' : undefined,
                  }}
                />
              )}
            />
          </>
        ) : (
          <>
            <Stack
              direction="row"
              alignItems={'center'}
              justifyContent={'space-between'}
              gap="1rem"
            >
              <h6 style={{ marginBlock: 0 }}>{name || 'NC'}</h6>
              <StatusTag
                color={typeOption?.color ?? '#676c79'}
                absolute={false}
                position="top"
                size="small"
              >
                {typeOption?.label ?? 'NC'}
              </StatusTag>
            </Stack>
            <Stack
              direction="row"
              alignItems={'center'}
              justifyContent={'space-between'}
              gap="1rem"
            >
              <span>{registrationInformation}</span>
              {maxParticipants ? (
                <span style={{ fontWeight: 'bold' }}>{maxParticipants} Max.</span>
              ) : (
                <div />
              )}
            </Stack>
            <span style={{ fontStyle: 'italic' }}>
              {deadline ? `Date limite: ${formatHumanDate(deadline)}` : null}
            </span>
          </>
        )}
      </Stack>
    </Stack>
  )
}
